import type { VehicleDealData } from '@/types/vehicle'
// import { merge as _merge, round as _round } from 'lodash-es'
import { acceptHMRUpdate, defineStore } from 'pinia'

interface DealState {
	deals: VehicleDealData[] | null
	product: VehicleDealData | null
	isLoading: boolean
	isIncompleteOrderModalOpen: boolean
	isOrderNoticeModalOpen: boolean
	isSubscribing: boolean
	isStartingOrder: boolean
	isVehicleAvailable: boolean
	existingOrder: ExistingOrder | null
	subscriptionDuration: number | null
	error: string | null
}

interface ApiResponse<T> {
	status: string | null
	proxy: unknown
	data: T
}

interface ExistingOrder {
	uuid?: string
	continue_url?: string
	order_url?: string
}

interface SubscribeResponse {
	status: boolean
	data: {
		method: string
		auth_url: string
		TL: string
		_redirect: string
		tl_expiry: string
		order_url: string
	}
}

interface GtmPayLoad {
	event: string
	product_category: string
	groupbuy_car_primary_url: string
	product: VehicleDealData
}

export const useGroupSubscription = defineStore('groupSubscription', {
	state: (): DealState => ({
		product: null,
		deals: [],

		isLoading: false,
		isIncompleteOrderModalOpen: false,
		isOrderNoticeModalOpen: false,
		isSubscribing: false,
		isStartingOrder: false,
		isVehicleAvailable: true,
		existingOrder: null,
		subscriptionDuration: null,
		error: null,
	}),

	getters: {
		getIsFeatured: (state) => {
			const featuredDeal =
				state.deals?.filter(
					(product) => product.is_featured === true
				) ?? null

			return featuredDeal?.[0] ?? null
		},
		getAllDeals: (state) => state.deals,
		getProduct: (state) => state.product,
		getIsLoading: (state) => state.isLoading,
	},

	actions: {
		async fetchAllGroupBuyOffers() {
			this.setLoadingState(true)

			try {
				const response = (await useFetchForApi(
					'/group-subscription/offers'
				)) as unknown as ApiResponse<VehicleDealData[]>

				if (response && response.data) {
					this.deals = response.data
				}
			} catch (error) {
				const message = (error as Error)?.message || String(error)
				this.handleError(message)
			} finally {
				this.setLoadingState(false)
			}
		},

		async fetchGroupBuyOfferBySlug(slug: string) {
			this.setLoadingState(true)

			try {
				const endpoint = `/group-subscription/offers/${slug}`
				const response = (await useFetchForApi(endpoint, {
					method: 'GET',
				})) as unknown as ApiResponse<VehicleDealData>

				if (response && response.data) {
					this.product = response.data
				}
			} catch (error) {
				const message = (error as Error)?.message || String(error)
				this.handleError(message)
			} finally {
				this.setLoadingState(false)
			}
		},

		async checkExistingOrders() {
			this.isSubscribing = true

			if (!isLoggedIn()) {
				this.isSubscribing = false
				this.redirectToSignIn()
				return
			}

			this.isSubscribing = false

			try {
				const { data } = (await useFetchForApi('/v2/pre-orders')) as {
					data: ExistingOrder[]
				}

				if (data[0]?.continue_url) {
					this.existingOrder = data[0]
					this.isIncompleteOrderModalOpen = true
					return
				}

				this.onModalOpen()
			} catch (error) {
				if ((error as { status?: number })?.status === 401) {
					this.redirectToSignIn()
					return
				}
				const message = (error as Error)?.message || String(error)
				this.handleError(message)
			}
		},

		onModalOpen() {
			this.isOrderNoticeModalOpen = true
			this.isVehicleAvailable = true
		},

		onModalClose() {
			if (this.isSubscribing) {
				return
			}
			this.isIncompleteOrderModalOpen = false
			this.isOrderNoticeModalOpen = false
			this.isStartingOrder = false
		},

		onResumeOrder() {
			this.isSubscribing = true
			const url =
				this.existingOrder?.continue_url ??
				this.existingOrder?.order_url
			if (url) {
				window.location.href = url
			}
		},

		async onStartNewOrder() {
			this.isSubscribing = true
			this.isStartingOrder = true

			await this.cancelExistingOrder()
			await this.subscribeToGroupBuy()
		},

		async subscribeToGroupBuy() {
			if (!this.product?.slug) {
				return
			}

			// Pass the captured referral code so the order's
			// lead source is derived the same way as a normal subscribe (product-details.js).
			const affiliateRefCode = useState('affiliate_ref_state')
			const hasRefQuery = affiliateRefCode.value

			try {
				const response = (await useFetchForApi(
					`/group-subscription/offers/${this.product.slug}/subscribe`,
					{
						method: 'POST',
						query: {
							...(hasRefQuery
								? { ref: affiliateRefCode.value }
								: {}),
						},
					}
				)) as unknown as SubscribeResponse

				if (response?.data?.order_url) {
					window.location.href = response.data.order_url
				}
			} catch (error) {
				this.isSubscribing = false
				this.isStartingOrder = false
				if ((error as { status?: number })?.status === 401) {
					this.redirectToSignIn()
					return
				}
				const message = (error as Error)?.message || String(error)
				this.handleError(message)
			}
		},

		async cancelExistingOrder() {
			if (!this.existingOrder?.uuid) {
				return
			}

			await useFetchForApi(
				`v2/presales/${this.existingOrder.uuid}/cancel`,
				{
					method: 'PATCH',
				}
			)
		},

		setLoadingState(status: boolean) {
			this.isLoading = status
		},

		async clearDeal() {
			this.deals = null
		},

		async reset() {
			this.clearDeal()
			this.setLoadingState(true)
		},

		redirectToSignIn() {
			const route = useRoute()
			const { signInUrl } = useAuthPaths(route.path)
			navigateTo(`${signInUrl.value}?_redirect=${route.path}`, {
				external: true,
			})
		},

		handleError(error: string) {
			console.error('[API Error]:', error)
			this.error = error
		},

		// GTM for group buy
		gtmTrack(payload: GtmPayLoad) {
			// console.log(payload)

			// payload.product can be a single product or an array of products
			const prod = Array.isArray(payload.product)
				? payload.product[0]
				: payload.product

			const data = {
				event: payload.event,
				product_category: payload.product_category,
				groupbuy_car_primary_url: payload?.groupbuy_car_primary_url,
				make: prod?.make,
				model: prod?.model,
				variant: prod?.variant,
				body_type: prod?.body_type,
				year: prod?.year,
				transmission: prod?.transmission,
				fuel_type: prod?.fuel_type,
				base_upfront_price: prod?.base_upfront_price,
				original_weekly_price: prod?.original_weekly_price,
				total_min_cost: prod?.total_min_cost,
			}

			trackGtmEvent(data)
		},
	},
})

if (import.meta.hot) {
	import.meta.hot.accept(
		acceptHMRUpdate(useGroupSubscription, import.meta.hot)
	)
}
